import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import { loadSession } from "../auth";
import { listTenants, listWorksites, adminListUsers } from "../services/api";
import { useAdminScope } from "../hooks/useAdminScope";
import { useTranslation, Trans } from "react-i18next";

export default function PlatformDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = loadSession()?.user;
  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));

  // Context selectors — persisted via useAdminScope
  const { tenantId, setTenantId, worksiteId, setWorksiteId } = useAdminScope(user?.id);

  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState("");

  const [worksites, setWorksites] = useState([]);
  const [worksitesLoading, setWorksitesLoading] = useState(false);
  const [worksitesError, setWorksitesError] = useState("");

  // Employees
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empError, setEmpError] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [meta, setMeta] = useState(null);

  // Load tenants on mount
  useEffect(() => {
    if (!isPlatformAdmin) {
      setTenantsLoading(false);
      return;
    }
    setTenantsLoading(true);
    setTenantsError("");
    listTenants()
      .then((data) => setTenants(data?.tenants || []))
      .catch((e) => setTenantsError(e?.message || t("platform_dashboard.error_tenants")))
      .finally(() => setTenantsLoading(false));
  }, [isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate saved tenantId once tenants are loaded
  useEffect(() => {
    if (!tenantId || tenants.length === 0) return;
    const ids = tenants.map((tn) => String(tn.tenant_id ?? ""));
    if (!ids.includes(String(tenantId))) setTenantId("");
  }, [tenants, tenantId, setTenantId]);

  // Load worksites when tenant changes
  useEffect(() => {
    setWorksites([]);
    setWorksitesError("");
    setEmployees([]);
    if (!tenantId) return;

    setWorksitesLoading(true);
    listWorksites({ tenantId })
      .then((data) => {
        const items = data?.items || [];
        setWorksites(items);
      })
      .catch((e) => setWorksitesError(e?.message || t("platform_dashboard.error_worksites")))
      .finally(() => setWorksitesLoading(false));
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate saved worksiteId and auto-select if exactly one worksite
  useEffect(() => {
    if (worksites.length === 0) return;
    if (worksiteId) {
      const ids = worksites.map((w) => String(w.id ?? w.worksite_id ?? ""));
      if (!ids.includes(String(worksiteId))) setWorksiteId("");
    } else if (worksites.length === 1) {
      setWorksiteId(String(worksites[0].id ?? worksites[0].worksite_id ?? ""));
    }
  }, [worksites, worksiteId, setWorksiteId]);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Reset page when tenant/worksite/search changes
  useEffect(() => {
    setPage(1);
  }, [tenantId, worksiteId, debouncedQ]);

  // Load employees when tenant/worksite/page/search changes
  useEffect(() => {
    if (!tenantId) return;
    setEmpLoading(true);
    setEmpError("");
    setEmployees([]);
    adminListUsers({ tenantId, worksiteId, q: debouncedQ, page, pageSize })
      .then(({ items, meta: m }) => {
        setEmployees(items || []);
        setMeta(m);
      })
      .catch((e) => setEmpError(e?.message || t("platform_dashboard.error_users")))
      .finally(() => setEmpLoading(false));
  }, [tenantId, worksiteId, debouncedQ, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isPlatformAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">
            <Trans i18nKey="platform_trainings.need_role">
              You need the <code>platform_admin</code> role to manage trainings.
            </Trans>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <h1 className="text-2xl font-bold text-slate-900">{t("platform_dashboard.title")}</h1>

      {/* Context selectors */}
      <Card>
        <div className="flex flex-wrap gap-4">
          {/* Tenant selector */}
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label className="block text-xs text-slate-500 font-medium mb-1">
              {t("platform_dashboard.tenant_label")}
            </label>
            {tenantsLoading ? (
              <p className="text-sm text-slate-500">{t("platform_dashboard.loading_tenants")}</p>
            ) : tenantsError ? (
              <p className="text-sm text-red-600">{tenantsError}</p>
            ) : (
              <select
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                <option value="">{t("platform_dashboard.select_tenant")}</option>
                {tenants.map((tn) => (
                  <option key={tn.tenant_id} value={tn.tenant_id}>
                    {tn.tenant_id} ({tn.user_count ?? 0})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Worksite selector — always visible, disabled until tenant selected */}
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label className="block text-xs text-slate-500 font-medium mb-1">
              {t("platform_dashboard.worksite_label")}
            </label>
            {tenantId && worksitesLoading ? (
              <p className="text-sm text-slate-500">{t("platform_dashboard.loading_worksites")}</p>
            ) : tenantId && worksitesError ? (
              <p className="text-sm text-red-600">{worksitesError}</p>
            ) : tenantId && worksites.length === 0 ? (
              <p className="text-sm text-slate-400">{t("platform_dashboard.no_worksites")}</p>
            ) : (
              <select
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:opacity-40"
                value={worksiteId}
                onChange={(e) => setWorksiteId(e.target.value)}
                disabled={!tenantId || worksites.length === 0}
              >
                <option value="">{t("platform_dashboard.all_worksites")}</option>
                {worksites.map((w) => {
                  const id = w.id ?? w.worksite_id ?? "";
                  return (
                    <option key={id} value={id}>
                      {w.name ?? id}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
        </div>
      </Card>

      {/* Employee list */}
      {!tenantId ? (
        <p className="text-sm text-slate-500">{t("platform_dashboard.select_tenant_first")}</p>
      ) : empLoading ? (
        <p className="text-sm text-slate-500">{t("platform_dashboard.loading_users")}</p>
      ) : empError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{empError}</p>
      ) : employees.length === 0 ? (
        <p className="text-sm text-slate-400">{debouncedQ ? "Nu au fost găsiți angajați pentru căutarea curentă." : t("platform_dashboard.no_employees")}</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
          {/* Top bar: search + optional pageSize selector (not visible on small screens) */}
          <div className="p-4 border-b border-slate-100 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Caută după nume sau email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="text-sm text-slate-600">{meta ? `Pagina ${meta.page} din ${meta.total_pages}` : ""}</div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_name")}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_email")}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_roles")}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_is_manager")}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("admin_users.table_actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {(u.roles || []).join(", ") || t("admin_users.no_roles")}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {u.derived?.isManager ? t("admin_users.yes") : t("admin_users.no")}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <Button variant="ghost" onClick={() => navigate(`/platform/users/${u.id}`)}>
                      {t("emp_details.btn_edit")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination controls */}
          {meta && (
            <div className="p-4 flex items-center justify-between">
              <div className="text-sm text-slate-600">{`Pagina ${meta.page} din ${meta.total_pages}`}</div>
              <div className="flex gap-2">
                <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!meta.has_prev}>
                  Anterior
                </Button>
                <Button onClick={() => setPage((p) => (meta.has_next ? p + 1 : p))} disabled={!meta.has_next}>
                  Următor
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
